
import { createContext, useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface UserProfile {
  profile_photo_url?: string;
  full_name?: string;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  uploadProfilePhoto: (file: File) => Promise<boolean>;
  changePassword: (newPassword: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
  uploadProfilePhoto: async () => false,
  changePassword: async () => false,
  logout: async () => {},
});

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile data
  const fetchUserProfile = async (userId: string) => {
    try {
      console.log('🔍 Fetching profile for user:', userId);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('profile_photo_url, role')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('❌ Error fetching user profile:', error);
        setUserProfile(null);
        return;
      }

      console.log('✅ Profile data fetched:', data);
      setUserProfile(data);
    } catch (error) {
      console.error('❌ Error in fetchUserProfile:', error);
      setUserProfile(null);
    }
  };

  // Refresh profile manually
  const refreshProfile = async () => {
    if (user?.id) {
      await fetchUserProfile(user.id);
    }
  };

  // Upload profile photo
  const uploadProfilePhoto = async (file: File): Promise<boolean> => {
    if (!user?.id) {
      console.error('❌ No user logged in');
      alert('You must be logged in to upload a photo');
      return false;
    }

    try {
      console.log('📤 Starting profile photo upload...');
      console.log('📁 File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      console.log('📂 Upload path:', filePath);

      // Upload file to Supabase Storage - using 'profile-photos' bucket
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('❌ Upload error details:', {
          message: uploadError.message,
          statusCode: uploadError.statusCode,
          error: uploadError
        });
        
        // Provide specific error messages
        if (uploadError.message.includes('not found')) {
          alert('Storage bucket not found. Please contact support.');
        } else if (uploadError.message.includes('permission')) {
          alert('Permission denied. Please contact support.');
        } else {
          alert(`Upload failed: ${uploadError.message}`);
        }
        return false;
      }

      console.log('✅ File uploaded successfully:', uploadData);

      // Get public URL
      const { data: urlData } = await supabase.storage
        .from('profile-photos')
        .getPublicUrl(filePath);

      const photoUrl = urlData.publicUrl;
      console.log('🔗 Public URL generated:', photoUrl);

      // Update user profile in database
      const { error: updateError } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          profile_photo_url: photoUrl,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (updateError) {
        console.error('❌ Profile update error:', updateError);
        alert(`Failed to update profile: ${updateError.message}`);
        return false;
      }

      console.log('✅ Profile photo updated successfully in database');
      
      // Immediately update the local state with the new photo URL
      setUserProfile(prev => ({
        ...prev,
        profile_photo_url: photoUrl
      }));
      
      console.log('✅ Profile photo state updated locally');
      
      return true;
    } catch (error) {
      console.error('❌ Unexpected error uploading profile photo:', error);
      alert(`An unexpected error occurred: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    }
  };

  // Change password
  const changePassword = async (newPassword: string): Promise<boolean> => {
    try {
      console.log('🔐 Changing password...');
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('❌ Password change error:', error);
        return false;
      }

      console.log('✅ Password changed successfully');
      return true;
    } catch (error) {
      console.error('❌ Error changing password:', error);
      return false;
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state change:', _event);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    console.log('🚪 Starting sign out process...');
    try {
      // Clear local state first
      setUser(null);
      setUserProfile(null);
      
      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('❌ Sign out error:', error);
        throw error;
      }
      
      console.log('✅ Sign out successful');
    } catch (error) {
      console.error('❌ Error during sign out:', error);
      // Even if there's an error, clear local state
      setUser(null);
      setUserProfile(null);
      throw error;
    }
  };

  const logout = signOut;

  return { 
    user, 
    userProfile, 
    loading, 
    signOut, 
    refreshProfile, 
    uploadProfilePhoto,
    changePassword,
    logout
  };
};
